import type { TFunction } from 'i18next'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { AmountCard, Button, Checkbox, EmptyState, Icon, Input, RadioCards, SavedCardSummary, Sheet, Skeleton } from '@/ds'
import type { AmountLine } from '@/ds'
import { LinkRow } from '@/app/components/LinkRow'
import { dateRange, formatMoney } from '@/lib/format'
import { useAppNavigate } from '@/lib/query-params'
import { useAsync } from '@/lib/useAsync'
import { pagosService } from '@/services/pagos'
import type { PaymentKind, PaymentSummary, TouristTax } from '@/services/pagos'
import type { Stay } from '@/services/reserva'
import { money } from '@/services/types'
import type { Money } from '@/services/types'
import { useApiLang } from '../Checkin/useSave'
import { SectionError } from '../Estancia/sections/SectionError'
import { CardChoice, choiceErrors, chosenLast4, initialChoice, toCardInput } from './CardChoice'
import type { CardChoiceValue } from './CardChoice'
import { PAY_ROUTES, plainText, readPartialEnabled, stayName, usePagosStay, OriginBackLink } from './shared'
import { usePaymentStart } from './usePaymentStart'

/** Qué se paga ahora: el anticipo si falta (obligatorio para confirmar la reserva) o todo lo pendiente. */
function amountFor(summary: PaymentSummary, choice: 'advance' | 'rest'): Money {
  if (choice === 'advance' && summary.advance?.amount) return summary.advance.amount
  return summary.pending
}

const advancePending = (s: PaymentSummary) => Boolean(s.advance && !s.advance.paid && s.advance.amount && s.advance.amount.amount > 0)

/** Filas del desglose: por concepto si el backend lo da (mock); si no, lo que da E2 (total y ya pagado). */
function breakdown(summary: PaymentSummary, lang: string, t: TFunction, onTaxInfo: (() => void) | null): AmountLine[] {
  if (summary.lines.length > 0) {
    return summary.lines.map((l) => ({
      label:
        l.kind === 'tourist_tax' && onTaxInfo ? (
          <span className="pagos__line-label">
            {l.concept}
            <button type="button" className="pagos__info-button" aria-label={t('v2.pagos.pendiente.queTasa')} onClick={onTaxInfo}>
              <Icon name="info" size={20} />
            </button>
          </span>
        ) : (
          l.concept
        ),
      // El PMS expresa el pago previo como una línea negativa para cuadrar el total. Para el huésped
      // es un importe ya abonado, así que se muestra sin signo y el total pendiente sigue siendo el de E2.
      amount: formatMoney(l.kind === 'paid' ? { ...l.amount, amount: Math.abs(l.amount.amount) } : l.amount, lang),
      ...(l.kind === 'paid' ? { tone: 'muted' as const } : {}),
    }))
  }
  const lines: AmountLine[] = [{ label: t('v2.legacy.payment.Booking_total'), amount: formatMoney(summary.total, lang) }]
  if (summary.paid.amount > 0) {
    const label = summary.advance?.paid && summary.advance.percent ? t('v2.pagos.pendiente.yaPagadoPorcentaje', { percent: summary.advance.percent }) : t('v2.pagos.pendiente.yaPagado')
    lines.push({ label, amount: formatMoney(summary.paid, lang), tone: 'muted' })
  }
  return lines
}

/** Validación de la tarjeta del canal (app actual ChannelValidation.tsx, D12): sin diseño propio, textos de la app actual. */
function ChannelValidation({ stay, summary, onNewCard }: { stay: Stay; summary: PaymentSummary; onNewCard: () => void }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const apiLang = useApiLang()
  const card = summary.savedCard
  const [cvv, setCvv] = useState('')
  const [showErrors, setShowErrors] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const { start, starting, error } = usePaymentStart(stay.guestCode)
  const amount = amountFor(summary, advancePending(summary) ? 'advance' : 'rest')
  const cvvError = card?.needsCvv && !/^\d{3,4}$/.test(cvv) ? (cvv ? t('v2.legacy.validations.Invalid') : t('v2.legacy.validations.Required')) : null
  const pay = () =>
    void start({ kind: 'channel', amount, returnTo: PAY_ROUTES.payments, last4: card?.last4 ?? null }, () => pagosService.pay(stay, { kind: 'channel', amount, card: { kind: 'saved', id: card?.id ?? '', cvv: card?.needsCvv ? cvv : null } }, apiLang)).then(() => setConfirm(false))
  return (
    <>
      <AmountCard
        total={formatMoney(amount, lang)}
        totalLabel={t('v2.legacy.payment.channelValidation.Total_pay_now')}
        lines={[
          { label: t('v2.legacy.labels.Stablishment'), amount: stay.property.name },
          { label: t('v2.legacy.labels.Number_of_reserve'), amount: stay.reference },
          { label: t('v2.legacy.payment.channelValidation.reserve_Total'), amount: formatMoney(summary.total, lang) },
        ]}
        totalRowLabel={t('v2.legacy.payment.channelValidation.Total_pay_now')}
        buttonLabel={card ? t('v2.legacy.payment.channelValidation.Authorize_the_payment_of_amount', { amount: formatMoney(amount, lang) }) : null}
        onPay={() => {
          setShowErrors(true)
          if (!cvvError) setConfirm(true)
        }}
        loading={starting}
      >
        <p>{t('v2.legacy.payment.channelValidation.Confirm_payment_with_the_card_you_entered_in_channel', { channel: summary.channelValidation.channelName ?? '' })}</p>
        {card && <SavedCardSummary last4={card.last4} expiry={card.expiry} holder={card.holder} />}
        {card?.needsCvv && (
          <>
            <Input label={t('v2.legacy.labels.CVV')} inputMode="numeric" autoComplete="cc-csc" maxLength={4} value={cvv} onChange={(e) => setCvv(e.target.value.replace(/\D/g, ''))} error={showErrors ? (cvvError ?? undefined) : undefined} className="tnum" />
          </>
        )}
        {error && (
          <p className="pagos__error" role="alert">
            {error}
          </p>
        )}
      </AmountCard>
      <button type="button" className="pagos__link-button" onClick={onNewCard}>
        {t('v2.legacy.payment.Introduce_new_card')}
      </button>
      <Sheet open={confirm} title={t('v2.pagos.pendiente.confirmarTitulo', { amount: formatMoney(amount, lang) })} confirmLabel={t('v2.pagos.pendiente.pagar', { amount: formatMoney(amount, lang) })} onConfirm={pay} onCancel={() => setConfirm(false)} loading={starting}>
        {card?.last4 ? t('v2.pagos.pendiente.confirmarGuardada', { last4: card.last4 }) : t('v2.pagos.pendiente.confirmarNueva')}
      </Sheet>
    </>
  )
}

function PendingForm({ stay, summary, tax, showReceiptNote }: { stay: Stay; summary: PaymentSummary; tax: TouristTax | null; showReceiptNote: boolean }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const apiLang = useApiLang()
  const { go } = useAppNavigate()
  const [which, setWhich] = useState<'advance' | 'rest'>(advancePending(summary) ? 'advance' : 'rest')
  const [choice, setChoice] = useState<CardChoiceValue>(() => initialChoice(summary))
  const [accepted, setAccepted] = useState(false)
  const [partial, setPartial] = useState(false)
  const [partialText, setPartialText] = useState('')
  const [showErrors, setShowErrors] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const [conditions, setConditions] = useState(false)
  const { start, starting, error } = usePaymentStart(stay.guestCode)
  const partialAvailable = readPartialEnabled(stay.guestCode) || Boolean(error)

  const partialAmount: Money | null = (() => {
    const n = Number(partialText.replace(',', '.'))
    return Number.isFinite(n) && n > 0 ? money(Math.round(n * 100), summary.pending.currency) : null
  })()
  const partialInvalid = partial && (!partialAmount || partialAmount.amount > summary.pending.amount)
  const kind: PaymentKind = partial ? 'partial' : which
  const amount = partial ? (partialAmount ?? money(0, summary.pending.currency)) : amountFor(summary, which)
  const amountText = formatMoney(amount, lang)
  const cardErrors = choiceErrors(summary, choice)
  const valid = Object.keys(cardErrors).length === 0 && accepted && !partialInvalid
  const last4 = chosenLast4(summary, choice)

  const pay = () =>
    void start({ kind, amount, returnTo: PAY_ROUTES.payments, last4 }, () => pagosService.pay(stay, { kind, amount, card: toCardInput(summary, choice) }, apiLang)).then(() => setConfirm(false))

  return (
    <>
      <AmountCard
        total={formatMoney(partial ? summary.pending : amountFor(summary, which), lang)}
        totalLabel={t('v2.pagos.pendiente.etiqueta')}
        totalRowLabel={t('v2.pagos.pendiente.totalFila')}
        lines={breakdown(summary, lang, t, tax ? () => go(PAY_ROUTES.tax) : null)}
        buttonLabel={null}
      />
      {advancePending(summary) && !partial && (
        <RadioCards
          label={t('v2.pagos.pendiente.cuanto')}
          value={which}
          onChange={(id) => setWhich(id as 'advance' | 'rest')}
          options={[
            { id: 'advance', label: t('v2.pagos.pendiente.soloAnticipo', { amount: formatMoney(amountFor(summary, 'advance'), lang) }) },
            { id: 'rest', label: t('v2.pagos.pendiente.todo', { amount: formatMoney(summary.pending, lang) }) },
          ]}
        />
      )}
      {partial && (
        <Input
          label={t('v2.legacy.payment.Amount_to_pay')}
          help={t('v2.legacy.payment.Partial_payment-description')}
          inputMode="decimal"
          className="tnum"
          value={partialText}
          onChange={(e) => setPartialText(e.target.value.replace(/[^\d.,]/g, ''))}
          error={showErrors && partialInvalid ? t('v2.legacy.validations.Invalid') : undefined}
          trailing={<span aria-hidden="true">€</span>}
        />
      )}
      <CardChoice summary={summary} value={choice} onChange={setChoice} showErrors={showErrors} disabled={starting} />
      <div className="pagos__group">
        <Checkbox checked={accepted} onChange={setAccepted}>
          <Trans
            i18nKey="v2.legacy.payment.Ive_read_booking_conditions"
            components={{
              2: summary.rateConditions ? (
                <button
                  type="button"
                  className="pagos__link-button"
                  onClick={(e) => {
                    e.preventDefault()
                    setConditions(true)
                  }}
                />
              ) : (
                <span />
              ),
            }}
          />
        </Checkbox>
        {showErrors && !accepted && (
          <span className="pagos__error" role="alert">
            {t('v2.pagos.pendiente.aceptaCondiciones')}
          </span>
        )}
      </div>
      <div className="pagos__group" aria-live="polite">
        {error && (
          <p className="pagos__error" role="alert">
            {error}
          </p>
        )}
        <Button
          loading={starting}
          onClick={() => {
            setShowErrors(true)
            if (valid) setConfirm(true)
          }}
        >
          {t('v2.pagos.pendiente.pagar', { amount: amountText })}
        </Button>
        {showReceiptNote && <p className="pagos__note">{t('v2.pagos.pendiente.nota')}</p>}
      </div>
      {partialAvailable && !partial && (
        <button type="button" className="pagos__link-button" onClick={() => setPartial(true)}>
          {t('v2.legacy.payment.Partial_payment')}
        </button>
      )}
      <Sheet open={confirm} title={t('v2.pagos.pendiente.confirmarTitulo', { amount: amountText })} confirmLabel={t('v2.pagos.pendiente.pagar', { amount: amountText })} onConfirm={pay} onCancel={() => setConfirm(false)} loading={starting}>
        {last4 ? t('v2.pagos.pendiente.confirmarGuardada', { last4 }) : t('v2.pagos.pendiente.confirmarNueva')}
      </Sheet>
      <Sheet open={conditions} title={t('v2.legacy.payment.Conditions')} confirmHidden onCancel={() => setConditions(false)}>
        <span className="pagos__pre">{plainText(summary.rateConditions ?? '')}</span>
      </Sheet>
    </>
  )
}

/**
 * 8.3 · Pago pendiente (Pagos.dc.html screen=pending, Bloque 3 §3a). Importes, tarjeta guardada y cobro reales
 * (E2 → E3 → banco → E4); desglose por concepto, tasa turística y recibo por email solo con mock (D36).
 */
export function PagoPendiente() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const apiLang = useApiLang()
  const stay = usePagosStay()
  const [summary, reload] = useAsync(`pagos:${stay.guestCode}:${apiLang}`, (signal) => pagosService.getPaymentSummary(stay, apiLang, signal))
  const [tax] = useAsync(`tasa:${stay.guestCode}`, (signal) => pagosService.getTouristTax(stay, signal))
  const [receipts] = useAsync(`recibos:${stay.guestCode}`, (signal) => pagosService.getReceipts(stay, signal))
  const [newCard, setNewCard] = useState(false)
  const s = summary.kind === 'ready' ? summary.value : null
  const taxValue = tax.kind === 'ready' ? tax.value : null
  const nothingPending = s !== null && s.pending.amount <= 0 && !advancePending(s)

  let lead = t('v2.pagos.pendiente.lead', { name: stayName(stay), dates: dateRange(stay.dates.checkIn, stay.dates.checkOut, lang) })
  if (s && !nothingPending && s.advance?.paid && s.advance.percent && s.advance.percent < 100) lead += ` ${t(taxValue ? 'v2.pagos.pendiente.leadRestoTasa' : 'v2.pagos.pendiente.leadResto', { percent: 100 - s.advance.percent })}`

  return (
    <main className="page pagos">
      <div className="pagos__head">
        <OriginBackLink />
        <h1>{t('v2.pagos.pendiente.titulo')}</h1>
        <p className="page__lead">{lead}</p>
      </div>
      {!stay.flags.paidWithGateway ? (
        <EmptyState icon="credit-card" title={t('v2.pagos.pendiente.sinPasarela')} headingLevel={2}>
          {t('v2.pagos.pendiente.sinPasarelaTexto')}
        </EmptyState>
      ) : summary.kind === 'loading' ? (
        <div aria-busy="true" className="pagos__stack">
          <Skeleton height={260} />
          <Skeleton height={176} />
        </div>
      ) : summary.kind === 'error' ? (
        <SectionError code={summary.code} onRetry={reload} />
      ) : s && nothingPending ? (
        <EmptyState icon="check-circle" title={t('v2.pagos.pendiente.nadaTitulo')} headingLevel={2}>
          {t('v2.pagos.pendiente.nadaTexto', { amount: formatMoney(s.total, lang) })}
        </EmptyState>
      ) : s && s.channelValidation.required && !newCard ? (
        <ChannelValidation stay={stay} summary={s} onNewCard={() => setNewCard(true)} />
      ) : s ? (
        <PendingForm stay={stay} summary={s} tax={taxValue} showReceiptNote={receipts.kind === 'ready'} />
      ) : null}
      {s && (
        <div className="pagos__stack">
          {taxValue && <LinkRow icon="file-text" title={t('v2.pagos.enlaces.tasa')} subtitle={formatMoney(taxValue.total, lang)} tabular to={PAY_ROUTES.tax} />}
          {s.savedCard && <LinkRow icon="credit-card" title={t('v2.pagos.enlaces.tarjeta')} subtitle={t('v2.pagos.enlaces.tarjetaSub')} to={PAY_ROUTES.card} />}
          {(stay.flags.showGetBills || receipts.kind === 'ready') && <LinkRow icon="file-text" title={t('v2.pagos.enlaces.recibos')} {...(receipts.kind === 'ready' ? { subtitle: t('v2.pagos.enlaces.recibosSub') } : {})} to={PAY_ROUTES.receipts} />}
        </div>
      )}
    </main>
  )
}
